import React, { useEffect, useState } from "react";

import { Box, CircularProgress, Modal } from "@mui/material";
import { clsx } from "clsx";

import { TeamsIntegrationModalContent } from "./TeamsIntegrationModalContent";
import { TeamsIntegrationModalHeader } from "./TeamsIntegrationmodalHeader";
import { useGetTeamsIntegrationModalSteps } from "./hook/useGetTeamsIntegrationModalSteps";
import { getTeamsImportStatus } from "@/api/useGetTeamsImportStatus";
import { usePostImportFilesFromTeams } from "@/api/usePostImportFilesFromTeams";
import { Alerts } from "@/components/shared/Alerts/Alerts";
import { PENDING_STATUS, UPLOAD_TEAMS_TASK, useBackground } from "@/context/BackgroundContext";
import { useTeamsAndChannels } from "@/context/TeamsAndChannelsContext";
import { useTeamsFiles } from "@/hooks/useTeamsFiles";
import { AlertColor } from "@/util/constants";
import { DocumentData } from "@/util/interfaces/DocumentData";
import { Channel, TeamsFile } from "@/util/interfaces/TeamsFile";
import { BaseTask } from "@/util/interfaces/background/BaseTask";

import styles from "./TeamsIntegrationModal.module.scss";

interface TeamsIntegrationModalProps {
  callbackClose: (_message?: string, _color?: AlertColor) => void;
  documents: DocumentData[];
}

export const TeamsIntegrationModal: React.FC<TeamsIntegrationModalProps> = ({
  callbackClose,
  documents,
}) => {
  const { addBackgroundTask } = useBackground();
  const { msalConfig } = useTeamsAndChannels();
  const { getSelectedFiles, teamsFiles, clearTeamsFiles, clearSelectedRows } = useTeamsFiles();
  const { mutateAsync: callUploadTeamsFiles } = usePostImportFilesFromTeams(msalConfig.backend_uri);
  const [selectedChannel, setSelectedChannel] = useState<Channel>();
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  useEffect(() => {
    // Cleanup function
    if (clearTeamsFiles) {
      clearTeamsFiles();
    }
    if (clearSelectedRows) {
      clearSelectedRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Helper function to build the hierarchical path
  const buildFilePath = (file: TeamsFile): string => {
    const pathParts: string[] = [];

    // Add teamName and channelName from the file
    const teamName = file.channel.teamName || "";
    const channelName = file.channel?.channelName || "";

    if (teamName) pathParts.push(teamName);
    if (channelName) pathParts.push(channelName);

    // Recursive function to get parent hierarchy
    const getParentPath = (currentFile: TeamsFile): string[] => {
      if (!currentFile.parentReference?.id) return [];

      const parent = teamsFiles.find((f) => f.id === currentFile.parentReference?.id);
      if (!parent) return [];

      // Recursively get parent path and add its name
      const parentPath = getParentPath(parent);
      return [...parentPath, parent.name];
    };

    // Add parent hierarchy
    const parentPath = getParentPath(file);
    pathParts.push(...parentPath);

    return pathParts.join("/") + "/";
  };
  const createBackgroundTask = (file: TeamsFile): BaseTask => {
    return {
      id: "", // Placeholder for id, will be set in context
      status: PENDING_STATUS,
      promise: callUploadTeamsFiles([file]),
      intervalId: setInterval(() => {}, 1000), // Placeholder, will be updated in context
      createdAt: new Date(),
      file: { name: file.name, id: file.uuid || "" },
      pollingConfig: {
        interval: 5000,
        shouldStartPolling: (response) => response?.data?.data?.id,
        pollingFunction: (response) => getTeamsImportStatus(response?.data?.data?.id),
        shouldContinuePolling: (response) => {
          if (response.httpStatus !== 200) return false;
          const files = response.data.result[0].files;
          return files.some((file: any) => file.status === "IN_PROGRESS");
        },
      },
    };
  };
  const uploadTeamsFiles = async () => {
    try {
      // Get files that have category and are not folders
      const files = teamsFiles
        .filter((f) => f.category && !f.folder)
        .map((file) => {
          const path = buildFilePath(file);
          const fileExists = documents.find(
            (eF) => eF.path === path && eF.documentName === file.name
          );
          return {
            ...file,
            path: path,
            uuid: fileExists ? fileExists.uuid : "",
          };
        });
      //Let's create the background task
      const backgroundTasks: BaseTask[] = files.map(createBackgroundTask);
      addBackgroundTask(backgroundTasks, UPLOAD_TEAMS_TASK);
    } catch {
      callbackClose(
        "Unexpected error while trying to upload files into the storage.",
        AlertColor.Error
      );
      return false;
    }
    callbackClose(
      "You have successfully imported item(s) from the desired Teams Channel.",
      AlertColor.Success
    );
    return true;
  };

  const {
    getSubmitButtonText,
    getCancelButtonText,
    handleSubmit,
    handleCancel,
    step,
    getSubmitButtonDisabled,
    loadingButton,
  } = useGetTeamsIntegrationModalSteps({ callbackClose, uploadTeamsFiles });

  const [isSubmitDisabled, submitDisableMessage] = getSubmitButtonDisabled(
    getSelectedFiles().filter((f) => !f.folder),
    isLoadingFolders
  );
  const [alertMessage, setAlertMessage] = useState("");
  useEffect(() => {
    setAlertMessage(submitDisableMessage ?? "");
  }, [submitDisableMessage]);
  return (
    <>
      <Modal open={true} onClose={() => callbackClose()} data-testid="modalTeamsIntegration">
        <Box
          className={clsx(
            {
              [styles.modal]: step === "fileList",
              [styles.warningModal]: step === "warning",
              [styles.metadata]: step === "metadata" || step === "review",
            },
            "modal-kb-content",
            styles.container
          )}
        >
          <TeamsIntegrationModalHeader step={step} callbackClose={callbackClose} />
          <TeamsIntegrationModalContent
            step={step}
            callbackClose={callbackClose}
            selectedChannel={selectedChannel}
            setSelectedChannel={setSelectedChannel}
            onLoadingChange={setIsLoadingFolders}
          />
          <Box className={styles.buttonsContainer}>
            <button
              data-testid="syncButton"
              className="save_btn"
              disabled={isSubmitDisabled || loadingButton}
              onClick={handleSubmit}
            >
              {loadingButton && (
                <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
                  <CircularProgress size={24} />
                  <span style={{ marginLeft: 10 }}>Uploading...</span>
                </div>
              )}
              {!loadingButton && getSubmitButtonText(step)}
            </button>
            <button
              data-testid="cancelButton"
              className="cancel_btn"
              onClick={handleCancel}
              disabled={loadingButton}
            >
              {getCancelButtonText(step)}
            </button>
          </Box>
        </Box>
      </Modal>
      <Alerts
        style={{
          top: "1%",
          right: "1%",
          left: "unset",
        }}
        alertOpen={alertMessage !== ""}
        color={AlertColor.Warning}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
